/**
 * Block-level review of a proposed document edit.
 * One hunk is one suggestion (a replace, an addition, or a removal).
 * Accepting a hunk writes that change and leaves the others waiting.
 */

export type BlockKind = 'same' | 'add' | 'remove'
export type HunkDecision = 'pending' | 'accepted' | 'rejected'

export type EditBlock = {
  id: string
  kind: BlockKind
  hunkId?: string
  text: string
}

export type PresentedBlock = {
  node: 'h1' | 'h2' | 'p' | 'ul'
  text?: string
  items?: string[]
}

function parseBlocks(markdown: string): string[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const blocks: string[] = []
  let index = 0
  const push = (text: string) => {
    const trimmed = text.replace(/\s+$/g, '')
    if (trimmed.length > 0) blocks.push(trimmed)
  }
  while (index < lines.length) {
    if (lines[index].trim() === '') {
      index += 1
      continue
    }
    if (lines[index].startsWith('```')) {
      const start = index
      index += 1
      while (index < lines.length && !lines[index].startsWith('```')) index += 1
      if (index < lines.length) index += 1
      push(lines.slice(start, index).join('\n'))
      continue
    }
    if (/^#{1,6}\s/.test(lines[index])) {
      push(lines[index])
      index += 1
      continue
    }
    if (/^(\s*[-*+]|\s*\d+\.)\s/.test(lines[index])) {
      const start = index
      while (
        index < lines.length &&
        (/^(\s*[-*+]|\s*\d+\.)\s/.test(lines[index]) ||
          (lines[index].trim() !== '' && /^\s+/.test(lines[index])))
      ) {
        index += 1
      }
      push(lines.slice(start, index).join('\n'))
      continue
    }
    const start = index
    while (
      index < lines.length &&
      lines[index].trim() !== '' &&
      !lines[index].startsWith('```') &&
      !/^#{1,6}\s/.test(lines[index]) &&
      !/^(\s*[-*+]|\s*\d+\.)\s/.test(lines[index])
    ) {
      index += 1
    }
    push(lines.slice(start, index).join('\n'))
  }
  return blocks
}

function diffBlockTexts(original: string[], proposed: string[]): Array<{ kind: BlockKind; text: string }> {
  const rows = original.length
  const cols = proposed.length
  const scores: number[][] = Array.from({ length: rows + 1 }, () => Array(cols + 1).fill(0))
  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = cols - 1; j >= 0; j -= 1) {
      scores[i][j] =
        original[i] === proposed[j]
          ? scores[i + 1][j + 1] + 1
          : Math.max(scores[i + 1][j], scores[i][j + 1])
    }
  }
  const out: Array<{ kind: BlockKind; text: string }> = []
  let i = 0
  let j = 0
  while (i < rows && j < cols) {
    if (original[i] === proposed[j]) {
      out.push({ kind: 'same', text: original[i] })
      i += 1
      j += 1
    } else if (scores[i + 1][j] >= scores[i][j + 1]) {
      out.push({ kind: 'remove', text: original[i] })
      i += 1
    } else {
      out.push({ kind: 'add', text: proposed[j] })
      j += 1
    }
  }
  while (i < rows) {
    out.push({ kind: 'remove', text: original[i] })
    i += 1
  }
  while (j < cols) {
    out.push({ kind: 'add', text: proposed[j] })
    j += 1
  }
  return out
}

export function reviewBlocks(original: string, proposed: string): EditBlock[] {
  const diff = diffBlockTexts(parseBlocks(original), parseBlocks(proposed))
  const blocks: EditBlock[] = []
  let hunk = -1
  let inChange = false
  diff.forEach((part, index) => {
    if (part.kind === 'same') {
      inChange = false
      blocks.push({ id: `b${index}`, kind: 'same', text: part.text })
      return
    }
    if (!inChange) {
      hunk += 1
      inChange = true
    }
    blocks.push({
      id: `b${index}`,
      kind: part.kind,
      hunkId: `h${hunk}`,
      text: part.text,
    })
  })
  return blocks
}

export function hunkIds(blocks: EditBlock[]): string[] {
  const ids: string[] = []
  for (const block of blocks) {
    if (block.hunkId && !ids.includes(block.hunkId)) ids.push(block.hunkId)
  }
  return ids
}

function blockText(block: EditBlock, overrides?: Record<string, string>): string {
  return overrides?.[block.id] ?? block.text
}

function joinBlocks(parts: string[]): string {
  return parts.join('\n\n')
}

/** Text to write now. Pending suggestions stay as the old wording. */
export function savedMarkdown(
  blocks: EditBlock[],
  decisions: Record<string, HunkDecision>,
  overrides?: Record<string, string>,
): string {
  const parts: string[] = []
  for (const block of blocks) {
    if (block.kind === 'same') {
      parts.push(block.text)
      continue
    }
    const decision = decisions[block.hunkId ?? ''] ?? 'pending'
    if (decision === 'accepted' && block.kind === 'add') parts.push(blockText(block, overrides))
    if (decision !== 'accepted' && block.kind === 'remove') parts.push(block.text)
  }
  return joinBlocks(parts)
}

/** What the review still offers after this round of decisions. */
export function remainingProposal(
  blocks: EditBlock[],
  decisions: Record<string, HunkDecision>,
  overrides?: Record<string, string>,
): string {
  const parts: string[] = []
  for (const block of blocks) {
    if (block.kind === 'same') {
      parts.push(block.text)
      continue
    }
    const decision = decisions[block.hunkId ?? ''] ?? 'pending'
    if (decision === 'rejected' && block.kind === 'remove') parts.push(block.text)
    if (decision === 'accepted' && block.kind === 'add') parts.push(blockText(block, overrides))
    if (decision === 'pending' && block.kind === 'add') parts.push(blockText(block, overrides))
  }
  return joinBlocks(parts)
}

export function presentBlock(text: string): PresentedBlock {
  const heading = text.match(/^(#{1,6})\s+([\s\S]*)$/)
  if (heading) {
    return { node: heading[1].length <= 1 ? 'h1' : 'h2', text: heading[2] }
  }
  const lines = text.split('\n')
  if (lines.every((line) => /^(\s*[-*+]|\s*\d+\.)\s/.test(line))) {
    return {
      node: 'ul',
      items: lines.map((line) => line.replace(/^(\s*[-*+]|\s*\d+\.)\s/, '')),
    }
  }
  return { node: 'p', text }
}

export function markdownFromPresentation(presented: PresentedBlock, raw: string): string {
  if (presented.node === 'h1') return `# ${presented.text ?? ''}`
  if (presented.node === 'h2') {
    const level = raw.match(/^(#{1,6})/)?.[1] ?? '##'
    return `${level} ${presented.text ?? ''}`
  }
  if (presented.node === 'ul') {
    return (presented.items ?? []).map((item) => `- ${item}`).join('\n')
  }
  return presented.text ?? ''
}
